import {
  UnorderedListOutlined as LadderIcon,
  UserOutlined as LessonIcon,
  TrophyOutlined as TournamentIcon,
} from '@ant-design/icons'
import {
  add,
  isWithinInterval as dateFnsIsWithinInterval,
  getHours,
  isBefore,
  isWeekend,
  parseISO,
  set,
  sub,
} from 'date-fns'
import { AUG, MARCH } from '../constants'
import { formatDateTime, isBetween, parseDateTime } from './dateTimeUtil'
import { getServerDate, isFuture, isPast, isToday, isTomorrow } from './serverTimeUtil'

import { Tag } from 'antd'
import _ from 'lodash'
import axios from '../axios/axios'
import { getTagColour } from '../components/booking/Booking'
import { CURRENT_YEAR } from './commonUtil'
import { notifyError } from './notifyUtil'
import { isBlank } from './stringUtil'

const FAR_FUTURE = new Date(2099, 1, 1)

export const getBookingScheduleInfo = (date) => {
  return { START_HOUR: 8, END_HOUR: 22.5, SLOT_DURATION: 30 }
}

// These need to match the server.
const CANCEL_BOOKING_GRACE_PERIOD = 15
const EDIT_BOOKING_GRACE_PERIOD = 15
const BOOKABLE_GRACE_PERIOD = 15
const LAST_MINUTE_BOOKING_PERIOD = 60 * 2 // 2 hours
const MAX_FUTURE_DAYS = 7
export const DEFAULT_MAX_BOOKING_MINUTES = 90

export const BOOKING_PERIOD_HOURS = 22
export const getBookingLimitMsg = (maxBookingTime = DEFAULT_MAX_BOOKING_MINUTES) =>
  `You can book up to ${maxBookingTime} minutes of court time every ${BOOKING_PERIOD_HOURS} hours`

const BOOKING_RULES_VIEWED_KEY = 'bookingRulesViewed-20250722'
export const setBookingRulesViewed = () => localStorage.setItem(BOOKING_RULES_VIEWED_KEY, 'yes')
export const bookingRulesViewed = (currentUser) =>
  currentUser == null ? true : localStorage.getItem(BOOKING_RULES_VIEWED_KEY) === 'yes'

export const BOOKING_VIEW_MODE = { DAY: 'DAY', WEEK: 'WEEK' }

export const getDayHeading = (date, large = true) =>
  isToday(date) ? 'Today' : formatDateTime(date, large ? 'EEEE' : 'EEE')

export const isEventAdminUser = (user) => user?.username === 'eventadmin'

export const getDisplayDate = (date, showFriendlyName = true) => {
  const dateStr = formatDateTime(date, 'EEEE MMMM d')
  let displayDate
  if (showFriendlyName && isToday(date)) {
    displayDate = `${dateStr} (Today)`
  } else if (showFriendlyName && isTomorrow(date)) {
    displayDate = `${dateStr} (Tomorrow)`
  } else {
    displayDate = dateStr
  }
  return displayDate
}

export const getCourtsText = (booking) => {
  if (!booking) {
    return ''
  }
  const courts = booking.courts
  const count = _.size(courts)
  if (count === 1) {
    const c = courts[0]
    return c.label || `Court ${c.id}`
  } else if (count > 1) {
    return 'Courts ' + _.join(_.map(booking.courts, 'id'), ', ')
  } else {
    return ''
  }
}

function capitalize(name) {
  name = _.trim(name ?? '')
  // Only uppercase the first letter of the name if the name is all lowercase,
  //  to avoid messing with names that are intentionally capitalized in a certain way (e.g. McDonald).
  if (name && !/[A-Z]/.test(name)) {
    name = _.upperFirst(name)
  }
  return name
}

export const getNames = (player) => {
  if (!player) {
    return { firstName: '', lastName: '' }
  }
  const firstName = capitalize(player.firstName)
  const lastName = capitalize(player.lastName)
  return { firstName, lastName }
}

/**
 * Returns the player's first name and last initial if available, falling back to just first or last or username.
 */
export const getPlayerFirstNameAndLastInitial = (player, includeDot = false) => {
  const suffix = includeDot ? '.' : ''
  var result
  const { firstName, lastName } = getNames(player)
  if (isBlank(firstName) && isBlank(lastName)) {
    result = player?.username
  } else {
    const lastInitial = _.isEmpty(lastName) ? '' : _.first(lastName)
    result = (firstName || '') + ' ' + (lastInitial + suffix || '')
  }
  if (_.isEmpty(_.trim(result))) {
    result = player?.username
  }
  return result
}

export const bookingOwnerIsAlsoParticipant = (booking) => {
  try {
    const ownerId = booking?.createdByUser?.id
    const participantIds = _.compact([
      booking?.player1?.id,
      booking?.player2?.id,
      booking?.player3?.id,
      booking?.player4?.id,
    ])
    return _.includes(participantIds, ownerId)
  } catch (e) {
    console.error(e)
    return false
  }
}

const getPlayerText = (booking, playerIndex) => {
  const guestName = booking[`guest${playerIndex}`]
  const player = booking[`player${playerIndex}`]
  const playerName = getPlayerFirstNameAndLastInitial(player) ?? guestName
  const isGuest = !!guestName
  if (!playerName) {
    return null
  }
  return isGuest ? `${playerName} (guest)` : playerName
}

export const getPlayersText = (booking, includeOwner = false) => {
  if (!booking) {
    return ''
  }
  const isTournamentMatch = booking.bookingType === 'Tournament'
  const creatorName = getPlayerFirstNameAndLastInitial(booking.createdByUser)
  const playerNames = [
    getPlayerText(booking, 1),
    getPlayerText(booking, 2),
    getPlayerText(booking, 3),
    getPlayerText(booking, 4),
  ]
  // Exclude empty names/players, and the name of the person that booked the court.
  const names = _.reject(
    playerNames,
    (playerText) => _.isNil(playerText) || (!includeOwner && playerText && playerText === creatorName),
  )

  const defaultResult = _.join(names, ', ')
  if (!isTournamentMatch) {
    return defaultResult
  } else {
    if (names.length === 2) {
      return `${names[0]} vs ${names[1]}`
    } else if (names.length === 4) {
      return `${names[0]}/${names[1]} vs ${names[2]}/${names[3]}`
    } else {
      return defaultResult
    }
  }
}

const isWithinInterval = (date, interval, atEndOfDay = true) => {
  const { start, end } = interval ?? {}
  const endAtEndOfDay = atEndOfDay ? set(end, { hours: 23, minutes: 59, seconds: 59 }) : end
  return dateFnsIsWithinInterval(date, { start, end: endAtEndOfDay })
}

export const isBallMachineAvailable = (bookingTime) => {
  return false
  if (!isBetween(bookingTime, new Date(CURRENT_YEAR, MARCH, 7), new Date(CURRENT_YEAR, AUG, 29))) {
    return false
  }
  const hour = getHours(bookingTime)
  return (isWeekend(bookingTime) && hour >= 15 && hour < 17) || (!isWeekend(bookingTime) && hour >= 13 && hour < 17)
}

export const isBallMachineBooked = async (bookingTime) => {
  try {
    const result = await axios.get(`/all/bookings/${formatDateTime(bookingTime, 'yyyyMMdd')}`)
    const bookings = result?.data

    const sameDayAndTime = (time1, time2) => {
      const format = 'yyyMMdd:HH:mm:ss'
      const one = formatDateTime(parseDateTime(time1), format)
      const two = formatDateTime(time2, format)
      return one === two
    }

    const existingBallMachineBooking = _.find(
      bookings,
      (booking) => booking.bookingType === 'Ball-Machine' && sameDayAndTime(booking.startTime, bookingTime),
    )

    return !_.isNil(existingBallMachineBooking)
  } catch (e) {
    notifyError('error', e)
  }
}

export const populateWithParent = (booking) => {
  const { parent } = booking
  if (!parent) {
    return booking
  }
  const { comment, description } = booking
  booking = {
    // Use most of the fields directly from this instance:
    ...booking,
    // Some fields always come from the parent:
    bookingType: parent.bookingType,
    // Some fields are defaulted from the parent if not present on the instance:
    comment: _.isEmpty(parent.comment) ? comment : parent.comment,
    description: _.isEmpty(parent.description) ? description : parent.description,
  }
  return booking
}

export const getMaxBookingTime = (user, maxBookingTime, bookingTimeRemaining, isLastMinute, date) => {
  const { START_HOUR, END_HOUR, SLOT_DURATION } = getBookingScheduleInfo(date)
  if (isLastMinute) {
    bookingTimeRemaining = maxBookingTime
  }
  const remaining = bookingTimeRemaining / SLOT_DURATION
  return user?.mayIgnoreFutureBookingLimit ? (END_HOUR - START_HOUR) * SLOT_DURATION + 1 : remaining + 1
}

export const getStartTimeAsDate = (booking) => (booking ? parseISO(booking.startTime) : null)

export const mayBeCancelled = (booking, user) => {
  if (!booking || !booking.createdByUser || !user) {
    return false
  }
  const createdByUser = booking.createdByUser.id === user.id
  const bookingCancelCutoff = add(getStartTimeAsDate(booking), { minutes: CANCEL_BOOKING_GRACE_PERIOD })
  const isFutureBooking = isFuture(bookingCancelCutoff)

  return (createdByUser && isFutureBooking) || user.mayCancelAnyBooking
}

export const userIsBookingOwner = (booking, user) => {
  if (!booking || !booking.createdByUser || !user) {
    return false
  }
  return booking.createdByUser.id === user.id
}

export const mayBeEdited = (booking, user) => {
  if (!booking || !booking.createdByUser || !user) {
    return false
  }
  const bookingEditCutoff = add(getStartTimeAsDate(booking), { minutes: EDIT_BOOKING_GRACE_PERIOD })
  const isFutureBooking = isFuture(bookingEditCutoff)

  return (userIsBookingOwner(booking, user) && isFutureBooking) || user.mayEditAnyBooking
}

export const getLatestAllowedBooking = (user) => {
  if (user && user.mayIgnoreFutureBookingLimit) {
    return FAR_FUTURE
  } else {
    return add(set(getServerDate(), { hours: 23, minutes: 59, seconds: 59 }), { days: MAX_FUTURE_DAYS })
  }
}

export const isTimeslotBookable = (time) => {
  if (_.isNil(time)) {
    return false
  }
  const earliestAllowed = add(time, { minutes: BOOKABLE_GRACE_PERIOD })
  return isFuture(earliestAllowed)
}

export const isFirstBookableTimeslot = (time) => {
  if (_.isNil(time)) {
    return false
  }

  const { SLOT_DURATION } = getBookingScheduleInfo(time)

  if (isTimeslotBookable(time)) {
    const prior = sub(add(time, { minutes: BOOKABLE_GRACE_PERIOD }), { minutes: SLOT_DURATION })
    return isPast(prior)
  } else {
    return false
  }
}

export const mayBeBooked = (time, user, court) => {
  if (_.isNil(time) || !user) {
    return false
  }
  if (user.mayIgnoreFutureBookingLimit) {
    return true
  }
  return isTimeslotBookable(time) && isBefore(time, getLatestAllowedBooking(user))
}

export const isLastMinute = (time) => {
  time = _.isDate(time) ? time : new Date(time)
  const lastMinuteAllowedInterval = {
    start: sub(getServerDate(), { minutes: BOOKABLE_GRACE_PERIOD }),
    end: add(getServerDate(), { minutes: LAST_MINUTE_BOOKING_PERIOD }),
  }
  const result = isWithinInterval(time, lastMinuteAllowedInterval, false)
  return result
}

export const getBookingTag = (booking, bp) => {
  if (!booking) {
    return null
  }
  if (booking.bookingType === 'General' || booking.bookingType === 'Blocked') {
    return null
  }
  const isXs = bp?.xs
  let bookingType = isXs ? booking.bookingType?.substring(0, 1) : _.lowerCase(booking.bookingType)
  if (booking.bookingType === 'Tournament') {
    bookingType = <TournamentIcon twoToneColor={'#fff'} title="Tournament match" />
  } else if (isXs) {
    if (booking.bookingType === 'Ladder') {
      bookingType = <LadderIcon twoToneColor={'#fff'} title="Ladder match" />
    } else if (booking.bookingType === 'Lesson') {
      bookingType = <LessonIcon twoToneColor={'#fff'} title="Lesson" />
    }
  }
  const result = (
    <Tag title={booking.bookingType} color={getTagColour(booking.bookingType)}>
      {bookingType}
    </Tag>
  )
  return result
}
